/**
 * NoteEditor.tsx — the HubSpot-pattern rich-text note composer (v0.6.5 split;
 * Jira-grade in UX Wave 4 §4; reworked to the HubSpot composer in UX Wave 5
 * §8, 2026-07-21 eve).
 *
 * Owner repo: @thegpservice/gpsclinics-portal. Slice: CRM pipeline (Wave 5).
 *
 * WHY: a contentEditable div with a toolbar using `document.execCommand`
 * (deprecated, but dependency-free and fine for a handful of formatting
 * commands — no rich-text library is warranted), submitting `innerHTML`. The
 * SERVER sanitises every note body before storing/re-serving it; this UI
 * applies a SECOND, client-side allowlist strip (`lib/sanitizeHtml`) before
 * ever handing a note's HTML to `dangerouslySetInnerHTML` — defence in
 * depth, not the only gate. The Link command only ever inserts an http(s)
 * URL (prompted, checked here AND stripped again by both sanitisers if
 * anything else slips in).
 *
 * WAVE-5 COMPOSER SHAPE (§8 — the CTO's HubSpot screenshot): the box shows
 * the placeholder "Start typing to leave a note…" until there is text; the
 * editor reads borderless until focus (the `.gps-composer` focus-within rule
 * in tokens.css); and the toolbar is PINNED AT THE BOTTOM of the editor, not
 * the top: B / I / U / clear-formatting / More (the list commands) /
 * insert link / the paperclip ATTACH button — attach is a toolbar icon,
 * not a separate zone. Roomy min-height for real note-taking.
 *
 * ATTACHMENTS (Wave 4, opt-in): when the caller passes an `attachments`
 * adapter, the toolbar grows the paperclip limited to the FOUR allowlisted
 * types (JPEG/PNG/WebP/PDF, max 5 per note). Each pick asks the adapter for
 * a signed PUT grant and uploads immediately; a `{stub: true}` grant (object
 * storage unset on this environment) shows a notice instead of attaching — a
 * missing secret must never break the note itself. The completed refs ride
 * `onSubmit`'s second argument so the caller can post them as
 * `attachmentRefs`.
 *
 * Posting is DELEGATED via `onSubmit(html, attachmentRefs)` so one editor
 * serves every flow: the composer's "Note" segment POSTs a timeline activity,
 * while the author-only edit PATCHes the existing note. A rejected `onSubmit`
 * renders the inline save error; a resolved one clears the box (and chips).
 */
import { useEffect, useRef, useState, type FormEvent, type ReactNode } from 'react';
import type { DealAttachmentContentType, PhotoUploadGrant } from '../../api/types';
import { uploadPhoto } from '../../api/journey';
import { sanitizeNoteHtml } from '../../lib/sanitizeHtml';
import { Button, ChevronDownIcon, CloseIcon, ErrorLine, LinkIcon, PaperclipIcon } from '../../ui/kit';
import { compactButtonStyle } from './shared';

/** The four content types the deal-attachment grant allows — enforced here too. */
const ATTACHMENT_TYPES: readonly DealAttachmentContentType[] = [
  'image/jpeg',
  'image/png',
  'image/webp',
  'application/pdf',
];

/** Attachments per note — mirrors the server's max5 gate. */
const MAX_ATTACHMENTS = 5;

/** The HubSpot-pattern empty-state prompt (Wave 5 §8 — exact copy). */
const PLACEHOLDER = 'Start typing to leave a note…';

/** How a caller opts the editor into attachments: grant one signed PUT per file. */
export interface NoteAttachmentsAdapter {
  requestGrant: (contentType: DealAttachmentContentType) => Promise<PhotoUploadGrant>;
}

function ToolbarButton({
  label,
  onClick,
  disabled,
  expanded,
  children,
}: {
  label: string;
  onClick: () => void;
  disabled?: boolean;
  /** For the disclosure trigger (More ▾) — reported so AT hears the state. */
  expanded?: boolean;
  children: ReactNode;
}) {
  return (
    <button
      type="button"
      aria-label={label}
      title={label}
      aria-expanded={expanded}
      disabled={disabled}
      // Prevent the editable box from losing its text selection before execCommand runs.
      onMouseDown={(e) => e.preventDefault()}
      onClick={onClick}
      style={{
        minWidth: '44px',
        minHeight: '44px',
        border: 0,
        borderRadius: 'var(--gps-r-sm)',
        background: 'none',
        color: 'var(--gps-text)',
        cursor: disabled ? 'not-allowed' : 'pointer',
        opacity: disabled ? 0.5 : 1,
        fontSize: '13.5px',
      }}
    >
      {children}
    </button>
  );
}

export function NoteEditor({
  formLabel,
  textboxLabel,
  submitLabel,
  initialHtml,
  attachments,
  onSubmit,
  onCancel,
}: {
  formLabel: string;
  textboxLabel: string;
  submitLabel: string;
  initialHtml?: string;
  /** Opt-in file attachments (Wave 4) — deal-note composer only today. */
  attachments?: NoteAttachmentsAdapter;
  onSubmit: (html: string, attachmentRefs: string[]) => Promise<void>;
  onCancel?: () => void;
}) {
  const editableRef = useRef<HTMLDivElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [empty, setEmpty] = useState(true);
  const [moreOpen, setMoreOpen] = useState(false);
  const [drafts, setDrafts] = useState<Array<{ ref: string; name: string }>>([]);
  const [uploading, setUploading] = useState(false);
  const [attachNotice, setAttachNotice] = useState<string | null>(null);
  const [attachError, setAttachError] = useState<string | null>(null);

  /** Keep the placeholder in lock-step with the box's ACTUAL text content. */
  function syncEmpty() {
    setEmpty(!(editableRef.current?.textContent?.trim()));
  }

  useEffect(() => {
    if (editableRef.current && initialHtml !== undefined) {
      editableRef.current.innerHTML = sanitizeNoteHtml(initialHtml);
      syncEmpty();
    }
  }, [initialHtml]);

  function exec(command: string, value?: string) {
    editableRef.current?.focus();
    // Guarded: execCommand is deprecated and some environments (incl. this
    // repo's jsdom test runner) don't implement it at all — never crash the
    // editor over a missing/optional formatting command.
    if (typeof document.execCommand === 'function') {
      document.execCommand(command, false, value);
    }
    syncEmpty();
  }

  /**
   * The Link command: a prompt keeps the text selection alive (an inline URL
   * input would steal focus and lose it). Only an http(s) URL is ever passed
   * to createLink — anything else is refused HERE, and the sanitiser strip
   * would drop it again anyway (defence in depth).
   */
  function insertLink() {
    const url = typeof window.prompt === 'function' ? window.prompt('Link URL (must start with http:// or https://)') : null;
    if (!url) return;
    if (!/^https?:\/\//i.test(url.trim())) {
      setError('Links must start with http:// or https://.');
      return;
    }
    setError(null);
    exec('createLink', url.trim());
  }

  /** One picked file → type check → grant → signed PUT → a removable chip. */
  async function handleFile(file: File | undefined) {
    if (!attachments || !file || uploading) return;
    setAttachError(null);
    if (!ATTACHMENT_TYPES.includes(file.type as DealAttachmentContentType)) {
      setAttachError('Only JPEG, PNG, WebP or PDF files can be attached.');
      return;
    }
    if (drafts.length >= MAX_ATTACHMENTS) {
      setAttachError(`Up to ${MAX_ATTACHMENTS} attachments per note.`);
      return;
    }
    setUploading(true);
    try {
      const grant = await attachments.requestGrant(file.type as DealAttachmentContentType);
      if (grant.stub || !grant.objectRef) {
        // Stub-safe: storage unset on this environment — the note still saves.
        setAttachNotice('File storage is not configured on this environment — the note will save without this attachment.');
        return;
      }
      await uploadPhoto(grant, file, file.type);
      const ref = grant.objectRef;
      setDrafts((prev) => [...prev, { ref, name: file.name }]);
    } catch {
      setAttachError('Sorry — we could not upload that file. Try again.');
    } finally {
      setUploading(false);
      // Allow re-picking the same file after a failure/removal.
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  }

  async function handleSubmit(event: FormEvent) {
    event.preventDefault();
    if (busy) return;
    const html = editableRef.current?.innerHTML ?? '';
    const textOnly = editableRef.current?.textContent?.trim() ?? '';
    if (!textOnly) {
      setError('Write a note before saving.');
      return;
    }
    setBusy(true);
    setError(null);
    try {
      await onSubmit(html, drafts.map((d) => d.ref));
      if (editableRef.current) editableRef.current.innerHTML = '';
      setDrafts([]);
      setAttachNotice(null);
      setEmpty(true);
    } catch {
      setError('Sorry — we could not save this note.');
    } finally {
      setBusy(false);
    }
  }

  return (
    <form onSubmit={handleSubmit} aria-label={formLabel}>
      {/* The composer surface: BORDERLESS until focus — the edge appears via
          tokens.css `.gps-composer:hover` / `:focus-within` (!important over
          this inline transparent edge, the repo's standard technique). The
          box comes FIRST and the toolbar is pinned at the BOTTOM (Wave 5 §8). */}
      <div
        className="gps-composer"
        style={{
          position: 'relative',
          border: '1.5px solid transparent',
          borderRadius: 'var(--gps-r-md)',
          background: 'var(--gps-surface)',
        }}
      >
        {empty ? (
          <span
            aria-hidden="true"
            style={{
              position: 'absolute',
              top: '12px',
              left: '14px',
              color: 'var(--gps-muted)',
              fontSize: '14px',
              pointerEvents: 'none',
            }}
          >
            {PLACEHOLDER}
          </span>
        ) : null}
        <div
          ref={editableRef}
          contentEditable
          role="textbox"
          aria-multiline="true"
          aria-label={textboxLabel}
          aria-placeholder={PLACEHOLDER}
          onInput={syncEmpty}
          style={{
            minHeight: '7rem',
            padding: '12px 14px',
            fontSize: '14px',
            color: 'var(--gps-text)',
            outline: 'none',
          }}
        />
        {attachments && drafts.length > 0 ? (
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap', padding: '0 10px 6px' }}>
            {drafts.map((draft) => (
              <span
                key={draft.ref}
                style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: '6px',
                  border: '1px solid var(--gps-border)',
                  borderRadius: 'var(--gps-r-sm)',
                  padding: '4px 8px',
                  fontSize: '12.5px',
                  color: 'var(--gps-text)',
                  background: 'var(--gps-surface-2)',
                }}
              >
                <PaperclipIcon size={12} /> {draft.name}
                <button
                  type="button"
                  aria-label={`Remove attachment ${draft.name}`}
                  onClick={() => setDrafts((prev) => prev.filter((d) => d.ref !== draft.ref))}
                  style={{ background: 'none', border: 0, cursor: 'pointer', color: 'var(--gps-muted)', font: 'inherit', padding: 0 }}
                >
                  <CloseIcon size={12} />
                </button>
              </span>
            ))}
          </div>
        ) : null}
        {/* The BOTTOM toolbar (Wave 5 §8): B / I / U / clear / More ▾ / link /
            📎 attach. The list commands live behind More ▾ — progressive
            disclosure keeps the resting row compact, HubSpot-style. */}
        <div
          role="toolbar"
          aria-label="Formatting"
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: '2px',
            flexWrap: 'wrap',
            padding: '4px 8px',
            borderTop: '1px solid var(--gps-border)',
          }}
        >
          <ToolbarButton label="Bold" onClick={() => exec('bold')}>
            <strong>B</strong>
          </ToolbarButton>
          <ToolbarButton label="Italic" onClick={() => exec('italic')}>
            <em>I</em>
          </ToolbarButton>
          <ToolbarButton label="Underline" onClick={() => exec('underline')}>
            <u>U</u>
          </ToolbarButton>
          <ToolbarButton label="Clear formatting" onClick={() => exec('removeFormat')}>
            <s>T</s>
            <sub>x</sub>
          </ToolbarButton>
          <ToolbarButton label="More formatting" expanded={moreOpen} onClick={() => setMoreOpen((v) => !v)}>
            More <ChevronDownIcon size={12} />
          </ToolbarButton>
          {moreOpen ? (
            <>
              <ToolbarButton label="Bullet list" onClick={() => exec('insertUnorderedList')}>
                •≡
              </ToolbarButton>
              <ToolbarButton label="Numbered list" onClick={() => exec('insertOrderedList')}>
                1.
              </ToolbarButton>
            </>
          ) : null}
          <ToolbarButton label="Link" onClick={insertLink}>
            <LinkIcon size={16} />
          </ToolbarButton>
          {attachments ? (
            // Wave 5 §8 — attach IS the paperclip in the toolbar, not a
            // separate drop zone. Same accessible name as before.
            <ToolbarButton
              label="Attach a file"
              disabled={busy || uploading || drafts.length >= MAX_ATTACHMENTS}
              onClick={() => fileInputRef.current?.click()}
            >
              <PaperclipIcon size={16} />
              {uploading ? '…' : null}
            </ToolbarButton>
          ) : null}
        </div>
      </div>
      {attachments ? (
        <>
          <input
            ref={fileInputRef}
            data-testid="note-attach-input"
            type="file"
            accept={ATTACHMENT_TYPES.join(',')}
            style={{ display: 'none' }}
            onChange={(e) => void handleFile(e.target.files?.[0])}
          />
          {attachNotice ? (
            <p role="status" style={{ margin: '6px 0 0', fontSize: '13px', color: 'var(--gps-muted)' }}>
              {attachNotice}
            </p>
          ) : null}
          {attachError ? <ErrorLine>{attachError}</ErrorLine> : null}
        </>
      ) : null}
      {error ? <ErrorLine>{error}</ErrorLine> : null}
      <div style={{ display: 'flex', gap: '8px', marginTop: 'var(--gps-s-2)' }}>
        <Button type="submit" variant="primary" disabled={busy || uploading} style={compactButtonStyle}>
          {busy ? 'Saving…' : submitLabel}
        </Button>
        {onCancel ? (
          <Button type="button" variant="ghost" onClick={onCancel} style={compactButtonStyle}>
            Cancel
          </Button>
        ) : null}
      </div>
    </form>
  );
}
